/**
 * Generative music box: a pentatonic random-walk lead over a slow two-voice
 * pad, scheduled with the standard WebAudio lookahead pattern. Zero bytes of
 * audio assets. Tempo breathes with the population; the world earns its
 * music at the first mitosis.
 */
import type { AudioEngine } from './engine';

const SCALE = [0, 2, 4, 7, 9]; // A major pentatonic degrees
const BASE = 220; // A3

function degreeToHz(degree: number): number {
  const octave = Math.floor(degree / SCALE.length);
  const idx = ((degree % SCALE.length) + SCALE.length) % SCALE.length;
  return BASE * Math.pow(2, octave + (SCALE[idx] as number) / 12);
}

export class MusicBox {
  private readonly out: GainNode;
  private timer = 0;
  private nextNoteAt = 0;
  private beat = 0;
  private degree = 5;
  private population = 1;
  private running = false;

  constructor(private readonly engine: AudioEngine) {
    this.out = engine.context.createGain();
    this.out.gain.value = 0;
    // mellow the whole instrument — no piercing partials survive this
    const warmth = engine.context.createBiquadFilter();
    warmth.type = 'lowpass';
    warmth.frequency.value = 2000;
    warmth.Q.value = 0.4;
    this.out.connect(warmth).connect(engine.ambientBus);
  }

  setPopulation(n: number): void {
    this.population = Math.max(1, n);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    const ctx = this.engine.context;
    this.nextNoteAt = ctx.currentTime + 0.2;
    // slow fade-in: the music arrives like weather
    this.out.gain.setTargetAtTime(0.5, ctx.currentTime, 6);
    this.timer = window.setInterval(() => this.schedule(), 25);
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    window.clearInterval(this.timer);
    this.out.gain.setTargetAtTime(0, this.engine.context.currentTime, 1.5);
  }

  private schedule(): void {
    const ctx = this.engine.context;
    if (ctx.state !== 'running') return;
    const horizon = ctx.currentTime + 0.12;
    while (this.nextNoteAt < horizon) {
      this.playBeat(this.nextNoteAt);
      const bpm = 56 + 4 * Math.log2(1 + this.population);
      this.nextNoteAt += 60 / bpm / 2; // eighth notes
      this.beat++;
    }
  }

  private playBeat(t: number): void {
    const ctx = this.engine.context;
    // lead: sparse, rounded plinks on a random walk — soft attack, mid
    // register, gentle overtone (instant onsets read as plosive clicks)
    if (Math.random() < 0.55) {
      this.degree += Math.random() < 0.5 ? -1 : 1;
      if (this.degree < 0) this.degree = 0;
      if (this.degree > 9) this.degree = 9;
      const hz = degreeToHz(this.degree);
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = hz;
      const partial = ctx.createOscillator();
      partial.type = 'sine';
      partial.frequency.value = hz * 3;
      const g = ctx.createGain();
      const pg = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.15, t + 0.025);
      g.gain.exponentialRampToValueAtTime(0.001, t + 1.6);
      pg.gain.setValueAtTime(0.0001, t);
      pg.gain.linearRampToValueAtTime(0.015, t + 0.03);
      pg.gain.exponentialRampToValueAtTime(0.0005, t + 0.5);
      osc.connect(g).connect(this.out);
      partial.connect(pg).connect(this.out);
      osc.start(t);
      partial.start(t);
      osc.stop(t + 1.7);
      partial.stop(t + 0.55);
    }
    // pad: chord change every 16 eighths, I -> vi -> IV -> V flavored
    if (this.beat % 16 === 0) {
      const roots = [0, 4, 3, 2];
      const root = roots[(this.beat / 16) % roots.length | 0] as number;
      for (const offset of [0, 2]) {
        const osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.value = degreeToHz(root + offset) / 2;
        osc.detune.value = offset === 0 ? -4 : 4;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.05, t + 2.5);
        g.gain.exponentialRampToValueAtTime(0.001, t + 8);
        osc.connect(g).connect(this.out);
        osc.start(t);
        osc.stop(t + 8.2);
      }
    }
  }
}
